import { Injectable } from '@angular/core';
import { Resolve, ActivatedRouteSnapshot, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, of, timer, throwError, timeout, Subject } from 'rxjs';
import { tap, catchError, map, filter, take, retry, delay, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import * as TemplateActions from '../../ngrx/template/template.actions';
import { AppState } from '../../ngrx/template/template.reducers';
import { isTemplateSlugReady } from '../../utils/template-cache.util';

@Injectable({ providedIn: 'root' })
export class TemplateresolverService implements Resolve<boolean> {
  private readonly TIMEOUT_MS = 5000; // 5 seconds total timeout
  private lastResolvedPath: string | null = null;
  private resolutionSubject = new Subject<string>();

  constructor(private store: Store<AppState>, private router: Router) {
    // Debounce resolution requests
    this.resolutionSubject.pipe(
      debounceTime(100),
      distinctUntilChanged()
    ).subscribe(path => {
      if (path === this.lastResolvedPath) {
        return;
      }

      this.lastResolvedPath = path;

      this.store
        .select((state) => state.template.slugs[path]?.loaded === true)
        .pipe(take(1))
        .subscribe((alreadyLoaded) => {
          if (!alreadyLoaded) {
            this.store.dispatch(TemplateActions.loadPageDataBySlug({ slug: path }));
          }
        });
    });
  }

  resolve(route: ActivatedRouteSnapshot): Observable<boolean> {
    // Use the incoming route snapshot (router.url still points to the previous URL while navigation is in-flight)
    const targetPath = route.url.map(segment => segment.path).join('/');
    const normalizedPath = targetPath.replace(/^\/+/, '');
    
    // Use subject to debounce and prevent duplicate requests
    this.resolutionSubject.next(normalizedPath);

    // Cached templates resolve synchronously — no need to block navigation.
    if (isTemplateSlugReady(this.store, normalizedPath)) {
      return of(true);
    }

    // Wait for the data to be loaded with retry mechanism
    return this.store.select(state => state.template.slugs[normalizedPath]).pipe(
      filter(slugData => slugData?.loaded === true),
      take(1),
      timeout(this.TIMEOUT_MS),
      retry({
        delay: (error, retryCount) => {
          return timer(10000); // Retry every 10 seconds
        }
      }),
      map(slugData => {
        return true;
      }),
      catchError((error) => {
        console.error("[TemplateResolver] Error resolving template:", error);
        
        // Check if it's a 404 error or timeout
        const isPageNotFound = error && 
          ((typeof error === 'object' && error.message === 'Page not found') ||
           (error instanceof Error && error.message === 'Page not found'));
           
        // Navigate to 404 page for all resolver errors
        this.router.navigate(['/404'], { replaceUrl: true });
        
        return of(false);
      })
    );
  }
}
